import {
  queryOptions,
  infiniteQueryOptions,
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  retrieveFile,
  updateFile,
  retrieveFileVersions,
  previewFileVersion,
  downloadFileVersion,
} from "@/lib/data/requests/files/canonical-files";
import { queryKeys } from "@/lib/data/static/query-keys";
import { deleteFile } from "@/lib/data/requests/files/file";

export const useUpdateCanonicalFile = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: Parameters<typeof updateFile>[1] & { id: string }) =>
      updateFile(id, body),
    onSuccess: () => invalidateFileQueries(client),
  });
};

export const useDeleteCanonicalFile = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: deleteFile,
    onSuccess: () => invalidateFileQueries(client),
  });
};

export const retrieveFileOptions = (id: string, enabled = true) => ({
  queryKey: [queryKeys.files, id, "metadata"],
  queryFn: () => retrieveFile(id),
  enabled: enabled && !!id,
  staleTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
});

export const fileQueryOptions = (id: string) =>
  queryOptions({
    queryKey: [queryKeys.files, id],
    queryFn: () => retrieveFile(id),
    enabled: !!id,
  });
export const fileVersionsQueryOptions = (id: string, enabled: boolean) =>
  infiniteQueryOptions({
    queryKey: [queryKeys.files, id, "versions"],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => retrieveFileVersions(id, pageParam),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
    retry: false,
  });
export const fileVersionPreviewQueryOptions = (
  id: string,
  versionId: string,
  contentType?: string,
) =>
  queryOptions({
    queryKey: [queryKeys.files, id, "preview", versionId, contentType],
    queryFn: async () =>
      contentType
        ? {
            ...(await downloadFileVersion(id, versionId)),
            mimeType: contentType,
          }
        : previewFileVersion(id, versionId),
    enabled: !!id && !!versionId,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchInterval: 4 * 60 * 1000,
    refetchOnWindowFocus: "always",
  });
export const invalidateFileQueries = async (client: QueryClient) => {
  await Promise.all(
    [
      queryKeys.files,
      queryKeys.firmFiles,
      queryKeys.matters,
      queryKeys.massTorts,
      queryKeys.matterFileImages,
    ].map((key) => client.invalidateQueries({ queryKey: [key] })),
  );
};
